'use client';

import { isLocaleCA } from '@libs/core';
import { useCallback } from 'react';
import { useScript } from '../useScript';
import { setRecaptchaValues } from './helpers';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 250;

export function useGoogleRecaptchaScore() {
    const siteKey =
        (isLocaleCA()
            ? process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CA
            : process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY) ?? '';

    const recaptchaSrcScore = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    const recaptchaStatusScore = useScript(recaptchaSrcScore);

    const isRecaptchaReady = (): boolean => recaptchaStatusScore === 'ready' && !!window.grecaptcha?.enterprise;

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const getRecaptchaToken = (action: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (!window.grecaptcha?.enterprise) {
                return reject(new Error('reCAPTCHA enterprise not available'));
            }

            window.grecaptcha.enterprise.ready(() => {
                window.grecaptcha?.enterprise.execute(siteKey, { action }).then(resolve).catch(reject);
            });
        });
    };

    const shouldRetry = (error: unknown, attempt: number): boolean => {
        const isBrowserError = typeof error === 'string' && error.includes('BROWSER_ERROR');
        return isBrowserError && attempt < MAX_RETRIES;
    };

    const executeRecaptchaScore = useCallback(
        async (action: string, attempt = 1): Promise<void> => {
            if (!isRecaptchaReady()) {
                return;
            }

            try {
                const token = await getRecaptchaToken(action);
                await setRecaptchaValues(token);
            } catch (error) {
                if (shouldRetry(error, attempt)) {
                    await delay(BASE_DELAY_MS * attempt);
                    return executeRecaptchaScore(action, attempt + 1);
                }

                throw error;
            }
        },
        [siteKey, recaptchaStatusScore]
    );

    return {
        recaptchaSrcScore,
        recaptchaStatusScore,
        executeRecaptchaScore,
        siteKey,
    };
}

export async function getRecaptchaTokenForApi(siteKey: string, action: string): Promise<string | undefined> {
    if (window.automatedTestRun) {
        return 'test-recaptcha-token';
    }

    if (!siteKey || !window.grecaptcha?.enterprise) {
        return undefined;
    }

    try {
        return new Promise((resolve, reject) => {
            window.grecaptcha?.enterprise.ready(() => {
                window.grecaptcha?.enterprise
                    .execute(siteKey, { action })
                    .then(resolve)
                    .catch((error: unknown) => {
                        reject(error);
                    });
            });
        });
    } catch {
        return undefined;
    }
}
